import React, { useEffect } from 'react';
import './ScenarioPanel.css';
import HelpTooltip from '../HelpTooltip/HelpTooltip';
import useDimensionOptions from '../../hooks/useDimensionOptions';
import useSharedForecastFilters, { DEFAULT_FORECAST_FILTERS } from '../../hooks/useSharedForecastFilters';
import { deriveRegionFromLocation, deriveGranularityFromLocation } from '../../utils_1/locationRouting';
import usePersistentState from '../../hooks/usePersistentState';
import { useAuth } from '../../context/AuthContext';
import { applyRoleLocks, getRoleLocks } from '../../utils_1/roleLocks';

const ScenarioPanel = ({ onRunScenario, loading, onContextChange }) => {
  const defaultScenario = {
    demand_multiplier: 1.0,
    price_sensitivity: 0.0,
    seasonality: 1.0,
    promotion_impact: 1.0
  };
  const [scenario, setScenario] = usePersistentState('scenario_panel_sliders', defaultScenario);

  const defaultParams = {
    ...DEFAULT_FORECAST_FILTERS,
    brand: '',
    sku_id: '',
    channel: '',
    location: ''
  };
  const [params, setParams] = useSharedForecastFilters([
    'scenario_panel_filters',
    'daily_forecast_filters',
    'promotional_analysis_filters',
  ]);
  const { user } = useAuth();
  const role = user?.role || 'demand_planner';
  const roleLocks = getRoleLocks(role);
  const brandLocked = Boolean(roleLocks.lockedBrand);
  const channelLocked = Boolean(roleLocks.lockedChannel);

  // Shared dimension options hook
  const { options, selectedBrand, setSelectedBrand } = useDimensionOptions(
    brandLocked ? roleLocks.lockedBrand : (params.brand || '')
  );
  const displayBrand = brandLocked ? roleLocks.lockedBrand : params.brand;
  const brandOptions = brandLocked && roleLocks.lockedBrand && !options.brands.includes(roleLocks.lockedBrand)
    ? [roleLocks.lockedBrand, ...options.brands]
    : options.brands;
  const channelOptions = channelLocked && roleLocks.lockedChannel && !options.channels.includes(roleLocks.lockedChannel)
    ? [roleLocks.lockedChannel, ...options.channels]
    : options.channels;

  useEffect(() => {
    if (brandLocked) {
      if (selectedBrand !== roleLocks.lockedBrand) {
        setSelectedBrand(roleLocks.lockedBrand);
      }
      if (params.brand !== roleLocks.lockedBrand) {
        setParams(prev => ({ ...prev, brand: roleLocks.lockedBrand, sku_id: '' }));
      }
      return;
    }
    if (selectedBrand !== params.brand) {
      setParams(prev => ({ ...prev, brand: selectedBrand, sku_id: '' }));
    }
  }, [brandLocked, params.brand, roleLocks.lockedBrand, selectedBrand, setParams, setSelectedBrand]);

  useEffect(() => {
    setParams(prev => {
      const next = applyRoleLocks(prev, role);
      const unchanged = Object.keys(next).every(key => next[key] === prev[key]);
      return unchanged ? prev : next;
    });
  }, [role, setParams]);

  useEffect(() => {
    if (!params.location) return;
    const derivedGranularity = deriveGranularityFromLocation(params.location);
    if (derivedGranularity && derivedGranularity !== params.forecast_granularity) {
      setParams(prev => ({ ...prev, forecast_granularity: derivedGranularity }));
    }
  }, [params.location, params.forecast_granularity, setParams]);

  useEffect(() => {
    onContextChange?.({
      filters: {
        brand: displayBrand || '',
        sku_id: params.sku_id || '',
        channel: params.channel || '',
        location: params.location || '',
        region: params.region || '',
        start_date: params.start_date || '',
        end_date: params.end_date || '',
        forecast_days: params.forecast_days,
        forecast_granularity: params.forecast_granularity || deriveGranularityFromLocation(params.location),
      },
      scenario: { ...scenario },
    });
  }, [displayBrand, onContextChange, params, scenario]);

  const handleSliderChange = (key, value) => {
    setScenario(prev => ({
      ...prev,
      [key]: parseFloat(value)
    }));
  };

  const handleParamChange = (key, value) => {
    setParams(prev => {
      const next = {
        ...prev,
        [key]: value
      };

      if (key === 'location') {
        const derivedRegion = deriveRegionFromLocation(value, options.locationRegionMap, options.regions);
        if (derivedRegion) {
          next.region = derivedRegion;
        }
        next.forecast_granularity = deriveGranularityFromLocation(value);
      }

      return next;
    });
  };

  const resetScenario = () => {
    setScenario(defaultScenario);
    setParams(prev => ({
      ...defaultParams,
      brand: brandLocked ? roleLocks.lockedBrand : '',
      channel: channelLocked ? roleLocks.lockedChannel : '',
      sku_id: '',
      location: '',
      region: '',
      forecast_granularity: '',
      promo_modeling_approach: 'separated'
    }));
    setSelectedBrand(brandLocked ? roleLocks.lockedBrand : '');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onRunScenario({ scenario, ...params });
  };

  return (
    <div className="scenario-panel card">
      <div className="panel-header">
        <h3 className="panel-title">🎯 Scenario Analysis</h3>
      </div>
      
      <form onSubmit={handleSubmit} className="scenario-form">
        <div className="form-grid grid-4" style={{ marginBottom: '20px' }}>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>Brand <HelpTooltip content="Select a specific brand to simulate, or leave as 'All Brands' to analyse the full portfolio." /></label>
            <select
              value={displayBrand}
              onChange={(e) => { handleParamChange('brand', e.target.value); setSelectedBrand(e.target.value); }}
              className="form-input"
              disabled={brandLocked}
            >
              <option value="">All Brands (Optional)</option>
              {brandOptions.map(brand => (
                <option key={brand} value={brand}>{brand}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>SKU ID <HelpTooltip content="Select a specific SKU to simulate. Pick a Brand first to filter this list." /></label>
            <select
              value={params.sku_id}
              onChange={(e) => handleParamChange('sku_id', e.target.value)}
              className="form-input"
            >
              <option value="">All SKUs (Optional)</option>
              {options.skus.map(sku => (
                <option key={sku} value={sku}>{sku}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>Channel <HelpTooltip content="Select a specific sales channel, or leave as 'All Channels'." /></label>
            <select
              value={params.channel}
              onChange={(e) => handleParamChange('channel', e.target.value)}
              className="form-input"
              disabled={channelLocked}
            >
              <option value="">All Channels (Optional)</option>
              {channelOptions.map(ch => (
                <option key={ch} value={ch}>{ch}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>Location <HelpTooltip content="Select a specific geographical location, or leave as 'All Locations'." /></label>
            <select
              value={params.location}
              onChange={(e) => handleParamChange('location', e.target.value)}
              className="form-input"
            >
              <option value="">All Locations (Optional)</option>
              {options.locations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="scenario-controls grid-4">
          {[
            {
              key: 'demand_multiplier',
              label: 'Demand Multiplier',
              emoji: '📈',
              tooltip: 'Adjust the simulated impact on baseline demand.',
              min: 0.5,
              max: 2.0,
              step: 0.1,
              colorObj: { color: '#3b82f6', glow: 'rgba(59, 130, 246, 0.2)' }
            },
            {
              key: 'price_sensitivity',
              label: 'Price Sensitivity',
              emoji: '💵',
              tooltip: 'Adjust the simulated negative impact of price increases on demand.',
              min: 0.0,
              max: 2.0,
              step: 0.1,
              colorObj: { color: '#10b981', glow: 'rgba(16, 185, 129, 0.2)' }
            },
            {
              key: 'seasonality',
              label: 'Seasonality',
              emoji: '🌤️',
              tooltip: 'Simulate the effect of varying seasonal demand factors.',
              min: 0.5,
              max: 2.0,
              step: 0.1,
              colorObj: { color: '#f59e0b', glow: 'rgba(245, 158, 11, 0.2)' }
            },
            {
              key: 'promotion_impact',
              label: 'Promotion Impact',
              emoji: '🎁',
              tooltip: 'Simulate the effect of promotional events on demand.',
              min: 0.5,
              max: 2.0,
              step: 0.1,
              colorObj: { color: '#8b5cf6', glow: 'rgba(139, 92, 246, 0.2)' }
            }
          ].map((config) => {
            const value = scenario[config.key];
            const percentage = ((value - config.min) / (config.max - config.min)) * 100;
            const { color, glow } = config.colorObj;
            
            return (
              <div key={config.key} className="slider-container" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div className="slider-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px', minHeight: '32px' }}>
                  <div className="slider-label-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                    <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{config.emoji}</span>
                    <label className="slider-label" style={{ 
                      margin: 0, 
                      fontWeight: 500, 
                      color: 'var(--gray-700)',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {config.label}
                    </label>
                    <HelpTooltip content={config.tooltip} />
                  </div>
                  <span style={{ fontWeight: 600, color: color, fontSize: '0.95rem', flexShrink: 0, marginLeft: '8px' }}>
                    {value.toFixed(2)}x
                  </span>
                </div>
                
                <div style={{ padding: '0 2px' }}>
                  <input
                    type="range"
                    min={config.min}
                    max={config.max}
                    step={config.step}
                    value={value}
                    onChange={(e) => handleSliderChange(config.key, e.target.value)}
                    className="slider"
                    style={{ 
                      '--slider-color': color,
                      '--slider-glow': glow,
                      background: `linear-gradient(to right, ${color} 0%, ${color} ${percentage}%, var(--gray-200) ${percentage}%, var(--gray-200) 100%)` 
                    }}
                  />
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#6b7280', marginTop: '-4px' }}>
                  <span>{parseFloat(config.min).toFixed(1)}x</span>
                  <span>{parseFloat(config.max).toFixed(1)}x</span>
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="scenario-actions">
          <button
            type="button"
            onClick={resetScenario}
            className="btn btn-secondary"
          >
            🔄 Reset
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? '⏳...' : '🎯 Run Analysis'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ScenarioPanel;
